import { type Failure, failureMessage } from './failure-message';

/** What a validator does with a failed check. */
export type FailureAction =
  | { readonly kind: 'report'; readonly message: string }
  | { readonly kind: 'retry' }
  | { readonly kind: 'unknown' };

const RETRYABLE_KINDS: ReadonlySet<string> = new Set([
  'network',
  'attempt-timeout',
  'timeout',
  'abort',
]);
const REPORTED_KINDS: ReadonlySet<string> = new Set([
  'parse',
  'validation',
  'too-large',
  'unsupported',
]);
const RETRYABLE_STATUSES: ReadonlySet<number> = new Set([
  408, 429, 502, 503, 504,
]);
const REPORTED_STATUSES: ReadonlySet<number> = new Set([400, 401, 403, 404]);

function classifyRefusal(
  failure: Failure & { refusalCode: string },
  permanentRefusalCodes: ReadonlySet<string>,
): FailureAction {
  if (permanentRefusalCodes.has(failure.refusalCode)) {
    return {
      kind: 'report',
      message: failureMessage({
        kind: 'http',
        refusalCode: failure.refusalCode,
        status: failure.status,
      }),
    };
  }
  return failure.refusalCode === 'auth-unavailable'
    ? { kind: 'retry' }
    : { kind: 'unknown' };
}

/**
 * Decides what a validator does with a described failure.
 *
 * @param failure - The description, or `undefined` when the caller could not describe it.
 * @param permanentRefusalCodes - Proxy refusal codes a filler cannot fix by trying again.
 * @returns `report` with a user-safe message, `retry` to let the submit through, or `unknown`.
 */
export function classifyFailure(
  failure: Failure | undefined,
  permanentRefusalCodes: ReadonlySet<string>,
): FailureAction {
  if (failure === undefined) return { kind: 'unknown' };
  if (failure.refusalCode !== undefined) {
    return classifyRefusal(
      { ...failure, refusalCode: failure.refusalCode },
      permanentRefusalCodes,
    );
  }
  if (RETRYABLE_KINDS.has(failure.kind)) return { kind: 'retry' };
  if (REPORTED_KINDS.has(failure.kind)) {
    return { kind: 'report', message: failureMessage({ kind: failure.kind }) };
  }
  if (failure.kind === 'http' && failure.status !== undefined) {
    if (RETRYABLE_STATUSES.has(failure.status)) return { kind: 'retry' };
    if (REPORTED_STATUSES.has(failure.status)) {
      return {
        kind: 'report',
        message: failureMessage({ kind: 'http', status: failure.status }),
      };
    }
  }
  return { kind: 'unknown' };
}
